import React from 'react'
import { cls } from '../../utils/helpers.js'

export type BtnVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'accent'
export type BtnSize = 'sm' | 'md' | 'lg'

export interface BtnProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children?: React.ReactNode
  variant?: BtnVariant
  size?: BtnSize
  loading?: boolean
  icon?: React.ReactNode
  className?: string
  /** Polymorphic element to render instead of <button> (e.g. 'a', Link). */
  as?: React.ElementType
}

export function Btn({
  children,
  variant = 'primary',
  size = 'md',
  loading,
  icon,
  className,
  as,
  disabled,
  ...props
}: BtnProps) {
  const base = 'btn'
  const variants: Record<BtnVariant, string> = {
    primary:   'btn-primary',
    secondary: 'btn-secondary',
    danger:    'btn-danger',
    ghost:     'btn-ghost',
    accent:    'btn-accent',
  }
  const sizes: Record<BtnSize, string> = { sm: 'btn-sm', md: '', lg: 'btn-lg' }
  const classes = cls(base, variants[variant], sizes[size], loading && 'btn-loading', className)
  const content = (
    <>
      {loading ? <span className="spinner" /> : icon && <span className="btn-icon">{icon}</span>}
      {children}
    </>
  )

  // Polymorphic render: when `as` is provided, render that element. Useful for
  // links that should look like buttons (e.g. download anchors) so we keep
  // hover/focus/active states + sizing consistent with real buttons.
  if (as) {
    const Tag = as
    return (
      <Tag className={classes} {...props}>
        {content}
      </Tag>
    )
  }

  return (
    <button
      className={classes}
      // W3: `disabled` is destructured out of props so this computed value is
      // authoritative — a co-passed `disabled` can no longer override the
      // loading-derived disable (which would reopen a double-submit window).
      disabled={loading || disabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </button>
  )
}
